import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { epicsApi } from '../api/endpoints/epicsApi';
import { Epic } from '../types/common';

interface EpicState {
  epics: Epic[];
  currentEpic: Epic | null;
  loading: boolean;
  error: string | null;
}

const initialState: EpicState = {
  epics: [],
  currentEpic: null,
  loading: false,
  error: null,
};

// Async thunks
export const fetchEpics = createAsyncThunk(
  'epics/fetchEpics',
  async (boardId: string, { rejectWithValue }) => {
    try {
      return await epicsApi.getEpics(boardId);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch epics'
      );
    }
  }
);

export const fetchEpic = createAsyncThunk(
  'epics/fetchEpic',
  async (epicId: string, { rejectWithValue }) => {
    try {
      return await epicsApi.getEpic(epicId);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch epic'
      );
    }
  }
);

export const createEpic = createAsyncThunk(
  'epics/createEpic',
  async (data: Partial<Epic>, { rejectWithValue }) => {
    try {
      return await epicsApi.createEpic(data);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to create epic'
      );
    }
  }
);

export const updateEpic = createAsyncThunk(
  'epics/updateEpic',
  async ({ epicId, data }: { epicId: string; data: Partial<Epic> }, { rejectWithValue }) => {
    try {
      return await epicsApi.updateEpic(epicId, data);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update epic'
      );
    }
  }
);

export const deleteEpic = createAsyncThunk(
  'epics/deleteEpic',
  async (epicId: string, { rejectWithValue }) => {
    try {
      await epicsApi.deleteEpic(epicId);
      return epicId;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to delete epic'
      );
    }
  }
);

const epicSlice = createSlice({
  name: 'epics',
  initialState,
  reducers: {
    setCurrentEpic: (state, action: PayloadAction<Epic | null>) => {
      state.currentEpic = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch epics
    builder.addCase(fetchEpics.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchEpics.fulfilled, (state, action) => {
      state.loading = false;
      state.epics = action.payload;
    });
    builder.addCase(fetchEpics.rejected, (state, action) => {
      state.loading = false;
      state.error = (action.payload as string) || action.error.message || 'Failed to fetch epics';
    });

    // Fetch single epic
    builder.addCase(fetchEpic.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchEpic.fulfilled, (state, action) => {
      state.loading = false;
      state.currentEpic = action.payload;
    });
    builder.addCase(fetchEpic.rejected, (state, action) => {
      state.loading = false;
      state.error = (action.payload as string) || action.error.message || 'Failed to fetch epic';
    });

    // Create epic
    builder.addCase(createEpic.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createEpic.fulfilled, (state, action) => {
      state.loading = false;
      state.epics.push(action.payload);
    });
    builder.addCase(createEpic.rejected, (state, action) => {
      state.loading = false;
      state.error = (action.payload as string) || action.error.message || 'Failed to create epic';
    });

    // Update epic
    builder.addCase(updateEpic.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(updateEpic.fulfilled, (state, action) => {
      state.loading = false;
      const index = state.epics.findIndex((epic) => String(epic.id) === String(action.payload.id));
      if (index !== -1) {
        state.epics[index] = action.payload;
      }
      if (String(state.currentEpic?.id) === String(action.payload.id)) {
        state.currentEpic = action.payload;
      }
    });
    builder.addCase(updateEpic.rejected, (state, action) => {
      state.loading = false;
      state.error = (action.payload as string) || action.error.message || 'Failed to update epic';
    });

    // Delete epic
    builder.addCase(deleteEpic.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(deleteEpic.fulfilled, (state, action) => {
      state.loading = false;
      state.epics = state.epics.filter((epic) => String(epic.id) !== String(action.payload));
      if (String(state.currentEpic?.id) === String(action.payload)) {
        state.currentEpic = null;
      }
    });
    builder.addCase(deleteEpic.rejected, (state, action) => {
      state.loading = false;
      state.error = (action.payload as string) || action.error.message || 'Failed to delete epic';
    });
  },
});

export const { setCurrentEpic, clearError } = epicSlice.actions;
export default epicSlice.reducer;
